import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { userApi, productsApi } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { IDS } from "@/constants/testIds";
import { Key, RefreshCw } from "lucide-react";

export default function Unlock() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const submit = async () => {
    const c = code.trim();
    if (!c) return;
    setBusy(true);
    try {
      const res = await userApi.verifyCode(c);
      await refresh();
      toast.success(res.message || "Access unlocked");
      nav(res.has_quiz_result ? "/insights" : "/quiz", { replace: true });
    } catch (e) {
      toast.error(e.message || "Invalid access code");
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    if (restoring) return;
    setRestoring(true);
    try {
      const res = await userApi.restorePurchases();
      const names = res.granted_product_names || [];
      if (names.length) {
        toast.success(
          names.length === 1
            ? `Added to your Library: ${names[0]}`
            : `${names.length} purchases added to your Library`
        );
        await refresh();
        // Route based on what got granted.
        const ids = res.granted_product_ids || [];
        if (ids.includes("quiz")) {
          nav("/quiz", { replace: true });
        } else {
          nav("/library", { replace: true });
        }
      } else {
        // Double-check current unlocks in case they're already verified but hit this page anyway.
        const unlocks = await productsApi.getMyUnlocks().catch(() => null);
        const hasQuiz = unlocks?.product_ids?.includes("quiz");
        if (hasQuiz) {
          await refresh();
          nav("/quiz", { replace: true });
          return;
        }
        toast.message("No purchases matched this email yet", {
          description:
            "If you just purchased, wait a moment and try again. Otherwise confirm you're signed in with the email you used at checkout.",
        });
      }
    } catch (e) {
      toast.error(e.message || "Could not check for purchases");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="max-w-md mx-auto" data-testid={IDS.unlockPage}>
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-3" style={{ background: "rgba(201,162,39,0.12)", border: "1px solid var(--pa-gold-dark)" }}>
          <Key color="var(--pa-gold)" />
        </div>
        <h1 className="pa-serif text-3xl" style={{ color: "var(--pa-text-primary)" }}>Unlock the app</h1>
        <p className="text-sm mt-2" style={{ color: "var(--pa-text-secondary)" }}>
          Signed in as <strong>{user?.email}</strong>
        </p>
      </div>

      {/* Primary path: automatic linking by email */}
      <div className="pa-card p-5 mb-4">
        <p className="text-sm font-semibold mb-2" style={{ color: "var(--pa-gold)" }}>
          I already purchased
        </p>
        <p className="text-xs mb-3" style={{ color: "var(--pa-text-secondary)" }}>
          If you used the same email at checkout, tap below and we&rsquo;ll link your purchase automatically &mdash; no code needed.
        </p>
        <button
          onClick={restore}
          disabled={restoring}
          className="pa-btn-gold w-full inline-flex items-center justify-center gap-2"
          data-testid={IDS.restorePurchasesBtn}
        >
          <RefreshCw size={16} className={restoring ? "animate-spin" : ""} />
          {restoring ? "Checking..." : "Link my purchase"}
        </button>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-3 my-5" aria-hidden="true">
        <div className="flex-1 h-px" style={{ background: "var(--pa-border)" }} />
        <span className="text-xs" style={{ color: "var(--pa-text-muted)" }}>or</span>
        <div className="flex-1 h-px" style={{ background: "var(--pa-border)" }} />
      </div>

      {/* Secondary path: manual code entry (fallback) */}
      <div className="pa-card p-5">
        <p className="text-sm font-semibold mb-2" style={{ color: "var(--pa-text-primary)" }}>
          Enter access code
        </p>
        <p className="text-xs mb-3" style={{ color: "var(--pa-text-secondary)" }}>
          Have a code from your confirmation email? Enter it here.
        </p>
        <input
          className="pa-input text-center tracking-widest text-lg pa-serif mb-3"
          placeholder="XXXX-XXXX"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          maxLength={30}
          data-testid={IDS.unlockInput}
          onKeyDown={(e) => e.key === "Enter" && submit()}
        />
        <button
          onClick={submit}
          disabled={busy || !code.trim()}
          className="pa-btn-outline w-full"
          data-testid={IDS.unlockSubmit}
        >
          {busy ? "Verifying..." : "Unlock with code"}
        </button>
      </div>

      <p className="text-xs mt-6 text-center" style={{ color: "var(--pa-text-muted)" }}>
        Don&rsquo;t have a purchase yet? Visit <strong>ParanormalArchetypes.net</strong> to get the assessment or the complete bundle.
      </p>
    </div>
  );
}
